import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ItemDetailActions } from './item-detail.actions';
import { ItemDetailSelectors } from './item-detail.selectors';
import {AsyncActionQueries, AsyncActionSelectors} from '@emm/core/util/async-action-state';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {AsyncActionStoreService} from '@emm/core/store/async-action-store.service';
import {getActionQueries} from '@emm/core/util/ngrx.util';
import {State} from '@emm/auth/store/auth.reducer';

export interface ItemDetailQueries extends AsyncActionQueries {
    selected$: Observable<ItemDetail>;
    selectedSubmenusPasteCascade$: Observable<[]>;
    selectedSubmenusDeleteCascade$: Observable<[]>;
    selectedSubmenusUpdateCascade$: Observable<[]>;
    isCopied: Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class ItemDetailStoreService extends AsyncActionStoreService<AsyncActionSelectors> {

    dispatch = {
        loadItemDetail: (path: string) => this.store.dispatch(ItemDetailActions.load({path})),
        updateMenuItem: (item: ItemDetail, updates: any, language: string, dispatchReload: boolean,
                         forceOverrideInheritance?: boolean, forceOverrideInheritanceMap?: any) =>
            this.store.dispatch(ItemDetailActions.updateMenuItem({item, updates, dispatchReload,
                forceOverrideInheritance, forceOverrideInheritanceMap})),
        addItem: (parent: ItemDetail, children: ItemDetail[], isLegacy: boolean) =>
            this.store.dispatch(ItemDetailActions.add({parent, children, isLegacy})),
        toggleItemDisabled: (path: string, updates: { disabled: boolean }, cascade: boolean,
                             forceOverrideInheritance: boolean) =>
            this.store.dispatch(ItemDetailActions.toggleDisabled({path, updates, cascade,
                forceOverrideInheritance})),
        deleteItem: (path: string, itemName: string, selectedItem: ItemDetail) =>
            this.store.dispatch(ItemDetailActions.delete({path, itemName, selectedItem})),
        deleteItemWithoutReload: (path: string, itemName: string) =>
            this.store.dispatch(ItemDetailActions.deleteWithoutReload({path, itemName})),
        pasteItemCascade: (submenuTag: string, parentTag: string, childTagPath: string, selectedItem: ItemDetail,
                           position: string) =>
            this.store.dispatch(ItemDetailActions.pasteCascade({submenuTag, parentTag, childTagPath, selectedItem,
            position})),
        deleteItemCascade: (path: string, selectedItem: ItemDetail) =>
            this.store.dispatch(ItemDetailActions.deleteCascade({path, selectedItem})),
        copyItem: (fromPath: string) =>
            this.store.dispatch(ItemDetailActions.copy({fromPath})),
        pasteItem: (fromPath: string, toPath: string) =>
            this.store.dispatch(ItemDetailActions.paste({fromPath, toPath})),
        moveItem: (fromPath: string, toPath: string, index: string) =>
            this.store.dispatch(ItemDetailActions.move({fromPath, toPath, index})),
        addLocalAttribute: (attributeIndex: number) =>
            this.store.dispatch(ItemDetailActions.addLocalAttribute({attributeIndex})),
        removeLocalAttribute: (attributeIndex: number) =>
            this.store.dispatch(ItemDetailActions.removeLocalAttribute({attributeIndex})),
        updateMenuItemCascade: (path: string, cascadePath: string | object, updates: any,
                                forceOverrideInheritance: boolean, forceOverrideInheritanceMap?: any) =>
            this.store.dispatch(ItemDetailActions.updateCascade({path, cascadePath, updates,
                forceOverrideInheritance, forceOverrideInheritanceMap})),
        updateFromLocaleSelected: (item: ItemDetail, dispatchReload) =>
            this.store.dispatch(ItemDetailActions.updateItemLocaleSuccess({item, dispatchReload})),
        selectCascadeSubmenus: (selectedSubmenuTag: string) =>
            this.store.dispatch(ItemDetailActions.selectCascadeSubmenus({selectedSubmenuTag})),
        deselectCascadeSubmenus: (selectedSubmenuTag: string) =>
            this.store.dispatch(ItemDetailActions.deselectCascadeSubmenus({selectedSubmenuTag})),
        resetCascadeSubmenus: () => this.store.dispatch(ItemDetailActions.resetCascadeSubmenus()),
    };

    queries: ItemDetailQueries = {
        selected$: this.select(ItemDetailSelectors.getValidSelectedItemDetail).pipe(
            distinctUntilChanged()
        ),
        selectedSubmenusPasteCascade$: this.select(ItemDetailSelectors.getSelectedCascadeSubmenus).pipe(
            distinctUntilChanged()
        ),
        selectedSubmenusDeleteCascade$: this.select(ItemDetailSelectors.getSelectedCascadeSubmenus).pipe(
            distinctUntilChanged()
        ),
        selectedSubmenusUpdateCascade$: this.select(ItemDetailSelectors.getSelectedCascadeSubmenus).pipe(
            distinctUntilChanged()
        ),
        isCopied: this.select(ItemDetailSelectors.getIsCopied).pipe(
            distinctUntilChanged()
        ),
        ...this.asyncActionQueries,
        ...getActionQueries({
            actionCreators: ItemDetailActions,
            actions$: this.actions$
        })
    };

    constructor(store: Store<State>, private actions$: Actions) {
        super(store, ItemDetailSelectors);
    }

}
